import React, { useCallback, useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';
import Input from 'src/components/form/floating-label/input';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import API from '../data/api';
import Modal from 'src/components/common/modal/modal';
import { env } from 'src/constants/env';
import { fireEvent } from 'src/utils/analytics/google';

// styles
import css from './propose-location.module.scss';
import 'src/utils/popup.scss';
import getValidationSchema from './validate';
import { toast } from 'react-toastify';

ProposeLocationPopup.propTypes = {
  trigger: PropTypes.element
};

export default function ProposeLocationPopup({ trigger }) {
  const validationSchema = getValidationSchema();
  const captchaRef = useRef(null);
  const registerAndClearOnChange = function (inputName) {
    return register(inputName, {
      onChange: () => {
        if (errors[inputName]) {
          clearErrors(inputName);
        }
      }
    });
  };

  const methods = useForm({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      title: '',
      discount: '',
      requirements: '',
      phone: '',
      address: '',
      url: '',
      description: ''
    }
  });
  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    getValues,
    formState: { errors, isSubmitSuccessful, defaultValues, isDirty, isValid }
  } = methods;
  const values = getValues();
  const isEdited = Object.keys(values).some((key) => values[key] !== defaultValues[key]);

  const resetCallback = useCallback(() => {
    return isSubmitSuccessful ? reset : () => {};
  }, [reset, isSubmitSuccessful]);

  useEffect(resetCallback, [resetCallback]);

  const onFormSubmit = async (data, close) => {
    try {
      const response = await API.proposeMapLocation(data);
      if (response.status === 201) {
        toast.success(`Пропозиція для створення локації надіслана! Модератор опрацює Ваш запит`);
        reset();
        close();
        fireEvent('propose_location_form_submit', data);
      }
    } catch (error) {
      toast.error('Виникла помилка. Спробуйте, будь ласка, пізніше.');
    }
  };

  const titleInputId = useId();
  const discountInputId = useId();
  const requirementsInputId = useId();
  const phoneInputId = useId();
  const addressInputId = useId();
  const urlInputId = useId();
  const descriptionInputId = useId();

  return (
    <Modal trigger={trigger} onClose={reset} nested closeOnDocumentClick={!isEdited}>
      {(close) => (
        <div className={css.proposeBusinessMapLocation}>
          <button className={css.close} onClick={close}>
            &times;
          </button>
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(async (data) => {
                const token = await captchaRef.current?.executeAsync();
                onFormSubmit({ ...data, captcha: token }, close);
              })}>
              <legend>
                <h1>Заповніть форму для створення локації</h1>
              </legend>
              <fieldset>
                <Input
                  label='Назва локації'
                  id={titleInputId}
                  name='title'
                  type='text'
                  error={errors?.title?.message}
                  placeholder='Моя локація'
                  required
                  {...registerAndClearOnChange('title')}
                />
                <Input
                  label='% Знижки'
                  id={discountInputId}
                  name='discount'
                  type='text'
                  placeholder='1-100%'
                  error={errors?.discount?.message}
                  required
                  {...registerAndClearOnChange('discount')}
                />
                <Input
                  label='Необхідні документи'
                  id={requirementsInputId}
                  name='requirements'
                  type='text'
                  placeholder='УБД, військовий квиток, тощо'
                  error={errors?.requirements?.message}
                  required
                  {...registerAndClearOnChange('requirements')}
                />
                <Input
                  label='Контактний тел.'
                  id={phoneInputId}
                  placeholder='+380... або 0800...'
                  name='phone'
                  type='phone'
                  error={errors?.phone?.message}
                  {...registerAndClearOnChange('phone')}
                />
                <Input
                  label='Адреса'
                  id={addressInputId}
                  name='address'
                  type='text'
                  placeholder='м. Київ, вул. Вулиця, буд. 10'
                  error={errors?.address?.message}
                  {...registerAndClearOnChange('address')}
                />
                <Input
                  label='Посилання на сайт'
                  id={urlInputId}
                  name='url'
                  type='text'
                  placeholder='http://example.com'
                  error={errors?.url?.message}
                  {...registerAndClearOnChange('url')}
                />
                <Input
                  label='Коментар'
                  id={descriptionInputId}
                  name='description'
                  multiline={true}
                  error={errors?.description?.message}
                  {...registerAndClearOnChange('description')}
                />
              </fieldset>
              <button disabled={!isDirty || !isValid} type='submit'>
                Надіслати
              </button>
            </form>
            <ReCAPTCHA
              ref={captchaRef}
              className={css.captcha}
              sitekey={env.REACT_APP_NZ_RECAPTCHA_SITE_KEY}
              hl='uk'
              size='invisible'
            />
          </FormProvider>
        </div>
      )}
    </Modal>
  );
}
